import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useLocalAgentId } from "../hooks/useLocalAgentId";

import AppShell from "../components/AppShell";
import ProvisioningNotice from "../components/ProvisioningNotice";
import { CardSkeleton } from "../components/Skeleton";
import { StatusDot, StatusPill } from "../components/StatusDot";
import { WarningIcon, BlockedIcon, InfoIcon } from "../components/Icons";
import { useAuth } from "../stores/auth";
import { formatRelative } from "../utils/format";
import {
  blockAgent,
  deleteAgent,
  getAgentRestartStatus,
  grantAgentAccess,
  listAgents,
  listPendingSessions,
  removeAgent,
  renameAgent,
  restartAgent,
  revokeAgentAccess,
  unblockAgent,
  type AgentRow,
  type DeleteWarning,
  type PendingSession,
  type TransferCandidate,
} from "../api/agents";

const POLL_INTERVAL_MS = 30_000;

// Restart-status polling. Much faster than the computer-list refresh above
// because we're showing a live "restarting…" state.
//
// This is watching for task_completed, NOT for the PC actually coming back
// online — those are different timelines. The agent's restart_agent handler
// acknowledges (and the loop persists + sends that event) BEFORE it ever
// exits, so "succeeded" here just means "the PC got the request," well
// before the Windows Service Control Manager's own restart delay (which can
// run up to a minute on a repeat-same-day crash — see setServiceRecovery)
// even starts. Budget: up to one heartbeat_interval (default 30s) to
// receive the task, then one fast-mode tick (5s) to report it back — 90s
// leaves generous margin over that ~35s expected case.
const RESTART_POLL_INTERVAL_MS = 2_000;
const RESTART_TIMEOUT_MS = 90_000;

// First agent release that understands a restart request (mirrors the
// backend's RESTART_AGENT_MIN_VERSION). Older PCs don't get the menu item.
const RESTART_MIN_VERSION = "0.13.12";

// True when `version` (any "-suffix" ignored) is at least `minimum`.
function versionAtLeast(version: string | undefined, minimum: string): boolean {
  const parse = (v: string) => v.split("-", 1)[0].split(".").map((p) => Number(p));
  const have = parse(version ?? "");
  const want = parse(minimum);
  if (have.some((n) => Number.isNaN(n)) || !version) return false;
  for (let i = 0; i < Math.max(have.length, want.length); i++) {
    const a = have[i] ?? 0;
    const b = want[i] ?? 0;
    if (a !== b) return a > b;
  }
  return true;
}

type RestartUiState = { status: "running" | "ok" | "error"; message: string };

type ConfirmState =
  | { type: "remove"; agent: AgentRow }
  | { type: "block"; agent: AgentRow }
  | { type: "restart"; agent: AgentRow }
  | {
      type: "delete";
      agent: AgentRow;
      warning?: DeleteWarning;
      transferCandidates?: TransferCandidate[];
      transferTo?: string;
    }
  | null;

export default function MyComputers() {
  const [rows, setRows] = useState<AgentRow[] | null>(null);
  const [pending, setPending] = useState<PendingSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const localAgentId = useLocalAgentId();
  // See auth.ts — office_code may be undefined for sessions persisted
  // before the migration ran; guard accordingly when rendering.
  const officeCode = useAuth((s) => s.tenant?.office_code);
  // Per-computer restart banner. Tracked outside AgentCard (like
  // PrinterSharing's test-print state) so it survives the 30s list refresh
  // re-rendering the card mid-poll.
  const [restartState, setRestartState] = useState<Map<string, RestartUiState>>(new Map());
  // Guards the restart poll loop against setState after the user navigates
  // away — a poll can run for up to RESTART_TIMEOUT_MS.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  function setRestartUiState(id: string, state: RestartUiState | null) {
    setRestartState((prev) => {
      const next = new Map(prev);
      if (state) next.set(id, state);
      else next.delete(id);
      return next;
    });
  }

  async function doRestart(agent: AgentRow) {
    setRestartUiState(agent.id, { status: "running", message: "Restarting…" });
    try {
      const ticket = await restartAgent(agent.id);
      const deadline = Date.now() + RESTART_TIMEOUT_MS;
      let finalStatus: Awaited<ReturnType<typeof getAgentRestartStatus>> | null = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, RESTART_POLL_INTERVAL_MS));
        if (!mountedRef.current) return;
        // A single dropped poll (office WiFi blip mid-restart — the exact
        // moment this network is least reliable) must not abort the whole
        // wait and show a false failure. Skip this iteration and try again
        // next tick; only running out of the overall deadline ends the loop.
        let s: Awaited<ReturnType<typeof getAgentRestartStatus>>;
        try {
          s = await getAgentRestartStatus(ticket.task_id);
        } catch {
          continue;
        }
        if (s.status === "succeeded" || s.status === "failed" || s.status === "expired" || s.status === "cancelled") {
          finalStatus = s;
          break;
        }
      }
      if (!mountedRef.current) return;
      if (finalStatus?.status === "succeeded") {
        setRestartUiState(agent.id, {
          status: "ok",
          message: "Restart requested — this computer should reconnect within a minute.",
        });
        fetchAgents();
      } else if (finalStatus?.status === "failed") {
        setRestartUiState(agent.id, {
          status: "error",
          message: finalStatus.error_message || "Couldn't restart this computer.",
        });
      } else {
        // Timed out waiting for an answer, or the task expired/was
        // cancelled before the agent picked it up. Not necessarily a
        // failure — the PC may still be restarting — so say so plainly
        // rather than guessing.
        setRestartUiState(agent.id, {
          status: "error",
          message: "No response yet. Check back in a minute — it may still be restarting.",
        });
      }
    } catch (e) {
      if (!mountedRef.current) return;
      setRestartUiState(agent.id, { status: "error", message: extractError(e) });
    }
  }

  async function fetchAgents() {
    try {
      const data = await listAgents();
      setRows(data.results);
      setError(null);
    } catch (e) {
      setError(extractError(e));
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function fetchOnce() {
      try {
        const [agentData, pendingData] = await Promise.all([
          listAgents(),
          listPendingSessions(),
        ]);
        if (!cancelled) {
          setRows(agentData.results);
          setPending(pendingData);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(extractError(e));
      }
    }
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  async function handleConfirm() {
    if (!confirm) return;
    if (confirm.type === "restart") {
      // Restart runs its own long-lived poll with a per-card banner (see
      // doRestart) rather than the "await then close" shape below — close
      // the dialog immediately and let it continue in the background.
      const agent = confirm.agent;
      setConfirm(null);
      void doRestart(agent);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      if (confirm.type === "remove") {
        await removeAgent(confirm.agent.id);
      } else if (confirm.type === "block") {
        await blockAgent(confirm.agent.id);
      } else if (confirm.type === "delete") {
        // First click: probe for dependents. If the server returns a
        // warning, render it and keep the dialog open — second click
        // either transfers ownership (transferTo set) or force-deletes.
        if (confirm.warning) {
          if (confirm.transferTo) {
            await deleteAgent(confirm.agent.id, { transferTo: confirm.transferTo });
          } else {
            await deleteAgent(confirm.agent.id, { force: true });
          }
        } else {
          const result = await deleteAgent(confirm.agent.id);
          if (result) {
            setConfirm({
              ...confirm,
              warning: result.would_delete,
              transferCandidates: result.transfer_candidates,
            });
            setBusy(false);
            return; // dialog stays open with the warning visible
          }
        }
      }
      setConfirm(null);
      await fetchAgents();
    } catch (e) {
      setActionError(extractError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleUnblock(agent: AgentRow) {
    setBusy(true);
    setActionError(null);
    try {
      await unblockAgent(agent.id);
      await fetchAgents();
    } catch (e) {
      setActionError(extractError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSetPrinterAccess(agent: AgentRow, allow: boolean) {
    setBusy(true);
    setActionError(null);
    try {
      if (allow) await grantAgentAccess(agent.id);
      else await revokeAgentAccess(agent.id);
      await fetchAgents();
    } catch (e) {
      setActionError(extractError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRename(agent: AgentRow, newLabel: string): Promise<string | null> {
    try {
      const result = await renameAgent(agent.id, newLabel);
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === agent.id ? { ...r, label: result.label } : r)) : prev,
      );
      return null;
    } catch (e) {
      return extractError(e);
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Computers</h1>
            <p className="mt-1 text-slate-600">
              Every computer in your office that's connected to KraetdNet.
            </p>
          </div>
          <Link
            to="/computers/add"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Add a computer
          </Link>
        </div>

        {error ? (
          <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}

        {actionError ? (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {actionError}
          </p>
        ) : null}

        {officeCode && (
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Your office code
              </p>
              <p className="font-mono text-lg font-bold tracking-widest text-slate-900">
                {officeCode}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(officeCode);
                setCodeCopied(true);
                window.setTimeout(() => setCodeCopied(false), 1500);
              }}
              className="no-tap-highlight rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {codeCopied ? "Copied!" : "Copy"}
            </button>
            <p className="basis-full text-xs text-slate-500">
              Share with anyone setting up a new computer. They enter this at{" "}
              <span className="font-mono">{window.location.host}/join</span> on the new
              PC, and it shows up below for you to approve.
            </p>
          </div>
        )}

        {pending.length > 0 && (
          <div className="mt-8">
            <p className="mb-3 text-sm font-medium text-slate-500">Waiting for your approval</p>
            <ul className="space-y-3">
              {pending.map((s) => (
                <PendingSessionCard key={`${s.kind}:${s.code}`} session={s} />
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8">
          {rows === null ? (
            <CardSkeleton count={3} />
          ) : rows.length === 0 && pending.length === 0 ? (
            <EmptyState />
          ) : rows.length === 0 ? null : (
            <ul className="space-y-3">
              {rows.map((row) => (
                <AgentCard
                  key={row.id}
                  row={row}
                  isLocal={row.id === localAgentId}
                  onRemove={() => setConfirm({ type: "remove", agent: row })}
                  onBlock={() => setConfirm({ type: "block", agent: row })}
                  onUnblock={() => handleUnblock(row)}
                  onDelete={() => setConfirm({ type: "delete", agent: row })}
                  onRestart={() => setConfirm({ type: "restart", agent: row })}
                  restartState={restartState.get(row.id) ?? null}
                  onClearRestartState={() => setRestartUiState(row.id, null)}
                  onRename={(label) => handleRename(row, label)}
                  onSetPrinterAccess={(allow) => handleSetPrinterAccess(row, allow)}
                  disabled={busy}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {confirm ? (
        <ConfirmDialog
          confirm={confirm}
          busy={busy}
          onConfirm={handleConfirm}
          onCancel={() => { setConfirm(null); setActionError(null); }}
          onTransferTargetChange={(transferTo) => {
            if (confirm.type === "delete") {
              setConfirm({ ...confirm, transferTo: transferTo || undefined });
            }
          }}
        />
      ) : null}
    </AppShell>
  );
}

function AgentCard({
  row,
  isLocal,
  onRemove,
  onBlock,
  onUnblock,
  onDelete,
  onRestart,
  restartState,
  onClearRestartState,
  onRename,
  onSetPrinterAccess,
  disabled,
}: {
  row: AgentRow;
  isLocal: boolean;
  onRemove: () => void;
  onBlock: () => void;
  onUnblock: () => void;
  onDelete: () => void;
  onRestart: () => void;
  restartState: RestartUiState | null;
  onClearRestartState: () => void;
  onRename: (newLabel: string) => Promise<string | null>;
  onSetPrinterAccess: (allow: boolean) => void;
  disabled: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.label || "");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function startEdit() {
    setDraft(row.label || "");
    setRenameError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setRenameError(null);
  }

  async function saveEdit() {
    const next = draft.trim();
    if (next === (row.label || "")) {
      cancelEdit();
      return;
    }
    setRenameBusy(true);
    const err = await onRename(next);
    setRenameBusy(false);
    if (err) {
      setRenameError(err);
      return;
    }
    setEditing(false);
  }

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const isBlocked = row.status === "blocked";
  const isOffline = row.status === "offline";
  const needsAttention = row.status === "needs_attention";
  const isProvisioning = row.status === "provisioning";
  const printerAccessOff = row.resource_access === "none";

  return (
    <li
      className={`rounded-2xl border border-slate-200 bg-white p-5 ${
        isOffline || isBlocked ? "opacity-70" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Status dot */}
        <div className="mt-1 shrink-0">
          <StatusDot status={row.status} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {editing ? (
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        else if (e.key === "Escape") cancelEdit();
                      }}
                      disabled={renameBusy}
                      maxLength={120}
                      placeholder={row.hostname}
                      aria-label="Computer name"
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-base font-semibold text-slate-900 focus:border-slate-500 focus:outline-none disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={renameBusy}
                      className="no-tap-highlight shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      {renameBusy ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={renameBusy}
                      className="no-tap-highlight shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                  {renameError ? (
                    <p className="mt-1 text-xs text-red-600" role="alert">{renameError}</p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">
                      Leave blank to use the computer's Windows name ({row.hostname}).
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/computers/${row.id}`}
                    className="block min-w-0 truncate text-base font-semibold text-slate-900 hover:underline"
                  >
                    {row.label || row.hostname}
                  </Link>
                  <button
                    type="button"
                    onClick={startEdit}
                    disabled={disabled}
                    aria-label="Rename this computer"
                    title="Rename"
                    className="no-tap-highlight shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
              )}
              {row.label && !editing ? (
                <div className="mt-0.5 text-xs text-slate-400 font-mono">{row.hostname}</div>
              ) : null}
              <div className="mt-0.5 text-sm text-slate-500">
                {row.os} {row.os_version} · last seen{" "}
                {row.last_seen_at ? formatRelative(row.last_seen_at) : "never"}
              </div>
              {row.tailscale_ip && row.status === "online" ? (
                <div className="mt-1 inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 text-xs text-slate-500">
                  <span className="font-mono">{row.tailscale_ip}</span>
                </div>
              ) : null}
              {row.client_cert_expires_at
                ? (() => {
                    const daysLeft = Math.floor(
                      (new Date(row.client_cert_expires_at).getTime() - Date.now()) / 86_400_000,
                    );
                    if (daysLeft > 30) return null;
                    const color =
                      daysLeft < 7
                        ? "border-red-200 bg-red-50 text-red-800"
                        : "border-amber-200 bg-amber-50 text-amber-800";
                    return (
                      <div
                        className={`mt-2 rounded-md border ${color} px-2.5 py-1 text-xs`}
                        title={`Cert expires ${row.client_cert_expires_at}`}
                      >
                        {daysLeft <= 0
                          ? "Connection certificate expired — this PC will re-enroll automatically"
                          : `Connection certificate expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (renews automatically)`}
                      </div>
                    );
                  })()
                : null}
              {row.last_heartbeat_error && row.status !== "online" ? (
                <div
                  className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800"
                  title={row.last_heartbeat_error_at ?? undefined}
                >
                  <span className="font-medium">Last error</span>
                  {row.last_heartbeat_error_at
                    ? <> · {formatRelative(row.last_heartbeat_error_at)}</>
                    : null}
                  <div className="mt-0.5 font-mono text-amber-900">{row.last_heartbeat_error}</div>
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {row.update_available && (
                <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                  Update available
                </span>
              )}
              {isLocal && (
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                  This computer
                </span>
              )}
              <StatusPill status={row.status} />

              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  disabled={disabled}
                  className="no-tap-highlight flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                  aria-label="Computer actions"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="8" cy="2.5" r="1.5" />
                    <circle cx="8" cy="8" r="1.5" />
                    <circle cx="8" cy="13.5" r="1.5" />
                  </svg>
                </button>

                {menuOpen ? (
                  <div className="absolute right-0 top-10 z-10 w-44 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                    {/* Hidden (not just disabled) while a restart for this
                        computer is already in flight — the confirm dialog
                        would otherwise let a second click fire a second
                        doRestart() poll loop racing the first over the same
                        restartState banner. */}
                    {row.status === "online" &&
                    restartState?.status !== "running" &&
                    versionAtLeast(row.agent_version, RESTART_MIN_VERSION) ? (
                      <>
                        <MenuItem
                          label="Restart KraetdNet"
                          onClick={() => { setMenuOpen(false); onRestart(); }}
                          variant="default"
                        />
                        <div className="my-1 border-t border-slate-100" />
                      </>
                    ) : null}
                    {isBlocked ? (
                      <MenuItem
                        label="Unblock"
                        onClick={() => { setMenuOpen(false); onUnblock(); }}
                        variant="default"
                      />
                    ) : (
                      <MenuItem
                        label="Block"
                        onClick={() => { setMenuOpen(false); onBlock(); }}
                        variant="default"
                      />
                    )}
                    {printerAccessOff ? (
                      <MenuItem
                        label="Allow printing"
                        onClick={() => { setMenuOpen(false); onSetPrinterAccess(true); }}
                        variant="default"
                      />
                    ) : (
                      <MenuItem
                        label="Turn off printing"
                        onClick={() => { setMenuOpen(false); onSetPrinterAccess(false); }}
                        variant="default"
                      />
                    )}
                    <MenuItem
                      label="Remove"
                      onClick={() => { setMenuOpen(false); onRemove(); }}
                      variant="default"
                    />
                    <div className="my-1 border-t border-slate-100" />
                    <MenuItem
                      label="Delete permanently"
                      onClick={() => { setMenuOpen(false); onDelete(); }}
                      variant="danger"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Offline callout */}
          {isOffline && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <WarningIcon />
              <div>
                <span className="font-medium">Can't connect</span> — this computer is currently off.
                <br />
                <span className="text-amber-700">
                  Turn it on to access its shared files and printers.
                </span>
              </div>
            </div>
          )}

          {/* Blocked callout */}
          {isBlocked && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-orange-50 px-3 py-2.5 text-sm text-orange-800">
              <BlockedIcon />
              <div>
                <span className="font-medium">Blocked</span> — this computer is not allowed to
                connect to the office network.
              </div>
            </div>
          )}

          {/* Needs attention callout */}
          {needsAttention && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-800">
              <InfoIcon />
              <div>
                <span className="font-medium">Needs attention</span> — KraetdNet may not be running
                correctly. Try restarting this computer.
              </div>
            </div>
          )}

          {/* Printer access turned off — owner-initiated, reversible.
              Shown so the owner can see at a glance which PCs are cut off
              from the shared printers. */}
          {printerAccessOff && !isBlocked && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
              <BlockedIcon />
              <div>
                <span className="font-medium">Printing turned off</span> — this computer can't print
                to the office's shared printers. Use "Allow printing" to turn it back on.
              </div>
            </div>
          )}

          {/* Restart-agent banner — running/ok/error, dismissible once
              terminal. Mirrors PrinterSharing's test-print result banner. */}
          {restartState && (
            <div
              className={`mt-3 flex items-start justify-between gap-2 rounded-xl px-3 py-2.5 text-sm ${
                restartState.status === "running"
                  ? "bg-slate-50 text-slate-700"
                  : restartState.status === "ok"
                    ? "bg-emerald-50 text-emerald-800"
                    : "bg-red-50 text-red-800"
              }`}
            >
              <span>{restartState.message}</span>
              {restartState.status !== "running" && (
                <button
                  type="button"
                  onClick={onClearRestartState}
                  aria-label="Dismiss"
                  className="no-tap-highlight shrink-0 text-xs underline"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}

          {/* Setting up notice — escalates after 2 / 5 min so a stuck
              PC tells the customer it's stuck instead of staying
              optimistic. Link points at the detail page where Resend
              Setup lives. */}
          {isProvisioning && (
            <ProvisioningNotice agentId={row.id} createdAt={row.created_at} />
          )}
        </div>
      </div>
    </li>
  );
}

function PendingSessionCard({ session }: { session: PendingSession }) {
  // /approve flow needs a 4-digit OOB the PC reads aloud; the legacy
  // /connect flow does not (owner confirms in their own browser).
  const href =
    session.kind === "pc_enrollment"
      ? `/approve/${session.code}`
      : `/connect/${session.code}`;
  const subline =
    session.kind === "pc_enrollment"
      ? "Waiting for your approval — needs a 4-digit code from the PC's screen."
      : "Waiting for your approval — setup is running on this computer right now.";
  return (
    <li className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <div className="flex items-center gap-4">
        <div className="flex h-2.5 w-2.5 shrink-0 items-center justify-center">
          <span className="block h-2.5 w-2.5 rounded-full bg-amber-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-slate-900">{session.hostname}</p>
              <p className="mt-0.5 text-sm text-amber-700">{subline}</p>
            </div>
            <Link
              to={href}
              className="shrink-0 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
            >
              Connect it
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
}

function MenuItem({
  label,
  onClick,
  variant,
}: {
  label: string;
  onClick: () => void;
  variant: "default" | "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`no-tap-highlight w-full px-4 py-2 text-left text-sm ${
        variant === "danger"
          ? "text-red-600 hover:bg-red-50"
          : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}

function ConfirmDialog({
  confirm,
  busy,
  onConfirm,
  onCancel,
  onTransferTargetChange,
}: {
  confirm: NonNullable<ConfirmState>;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onTransferTargetChange: (transferTo: string) => void;
}) {
  const name = confirm.agent.label || confirm.agent.hostname;

  // When the delete probe came back with dependents, render a stronger
  // warning + a list of what's about to be destroyed. Second click on
  // "Delete permanently" then issues with force=true, or "Transfer & delete"
  // reassigns shares/printers/backups to the picked candidate first.
  const warning = confirm.type === "delete" ? confirm.warning : undefined;
  const candidates = confirm.type === "delete" ? confirm.transferCandidates ?? [] : [];
  const transferTo = confirm.type === "delete" ? confirm.transferTo : undefined;
  const transferring = Boolean(transferTo);

  const copy = {
    remove: {
      title: "Remove this computer?",
      body: `"${name}" will be hidden from your list and disconnected from the office network. You can still re-enroll it later with a new code.`,
      confirm: "Remove",
      confirmClass: "bg-slate-800 hover:bg-slate-700 text-white",
    },
    block: {
      title: "Block this computer?",
      body: `"${name}" will be prevented from connecting to the office network. It will stay in your list so you can unblock it later.`,
      confirm: "Block",
      confirmClass: "bg-amber-600 hover:bg-amber-500 text-white",
    },
    restart: {
      title: "Restart KraetdNet on this computer?",
      body: `KraetdNet on "${name}" will briefly disconnect and reconnect — around a minute. Anything mid-print or mid-transfer through it right now may need to be retried.`,
      confirm: "Restart",
      confirmClass: "bg-slate-800 hover:bg-slate-700 text-white",
    },
    delete: transferring
      ? {
          title: "Permanently delete this computer?",
          body: `"${name}" will be deleted. Its shared folders, printers, and backup jobs will move to the computer you picked.`,
          confirm: "Transfer & delete",
          confirmClass: "bg-slate-900 hover:bg-slate-700 text-white",
        }
      : {
          title: "Permanently delete this computer?",
          body: `"${name}" and all its history will be deleted and cannot be recovered. The computer will need to be re-enrolled with a new code if you want to add it back.`,
          confirm: "Delete permanently",
          confirmClass: "bg-red-600 hover:bg-red-500 text-white",
        },
  }[confirm.type];

  const warningRows = warning
    ? [
        { count: warning.shares_hosted, label: "shared folder(s) hosted here" },
        { count: warning.printers_hosted, label: "shared printer(s) hosted here" },
        { count: warning.backup_jobs, label: "backup job(s)" },
        { count: warning.backup_runs, label: "backup history record(s)" },
        { count: warning.mounted_drives, label: "drive(s) this PC has mounted" },
        { count: warning.installed_printers, label: "shared printer(s) installed on this PC" },
      ].filter((r) => r.count > 0)
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-base font-semibold text-slate-900">
          {warning ? "Are you sure?" : copy.title}
        </h2>
        <p className="mt-2 text-sm text-slate-600">{copy.body}</p>
        {warning ? (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            <p className="font-medium">Deleting will also remove:</p>
            <ul className="mt-2 list-disc space-y-0.5 pl-5">
              {warningRows.map((r) => (
                <li key={r.label}>
                  <span className="font-semibold">{r.count}</span> {r.label}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-red-800">
              This can't be undone. Click "Delete permanently" again to confirm.
            </p>
          </div>
        ) : null}
        {warning && candidates.length > 0 ? (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-800">
              Move everything to another computer instead?
            </p>
            <p className="mt-1 text-xs text-slate-600">
              Shared folders, printers, and backup jobs will be reassigned. Other PCs
              re-connect automatically.
            </p>
            <label className="mt-2 block text-xs font-medium text-slate-700">
              Transfer to:
              <select
                value={transferTo ?? ""}
                onChange={(e) => onTransferTargetChange(e.target.value)}
                disabled={busy}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none disabled:opacity-60"
              >
                <option value="">Don't transfer — delete everything</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.label || c.hostname)}
                    {c.status === "online" ? "" : ` · ${c.status}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="no-tap-highlight rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`no-tap-highlight rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${copy.confirmClass}`}
          >
            {busy ? "Please wait…" : copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No computers yet</h2>
      <p className="mt-2 text-sm text-slate-600">
        Click "Add a computer" to get a code, then install KraetdNet on the computer.
      </p>
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return "Something went wrong. Please try again.";
}
